/**
 * Utilidades para mantener sanas las conexiones (`questionLogic`) de una encuesta
 * cuando se saca una pregunta de la lista.
 *
 * Las conexiones no guardan ids: apuntan a la posición 1-based de la pregunta
 * (`identifier`). Por eso, al borrar una pregunta del medio no alcanza con
 * sacarla del arreglo: hay que renumerar las que quedan y reengancharlas, de
 * manera que la que apuntaba a la eliminada pase a apuntar a donde apuntaba
 * ella (1 -> 2 -> 3, se borra la 2, queda 1 -> 3).
 */

const END = 'end';

const sameIdentifier = (a, b) => `${a}` === `${b}`;

const isEmptyTarget = (target) => target === undefined || target === null || target === '';

/**
 * Destino que heredan las preguntas que apuntaban a la eliminada: su salto por
 * defecto (el que no depende de una alternativa) o, si no tiene uno, el último
 * de sus saltos, que es el que la UI usa como "el resto de las alternativas".
 *
 * @param {object} deletedQuestion Pregunta que se está eliminando.
 * @returns {string} Identificador destino, en la numeración vieja.
 */
const getInheritedTarget = (deletedQuestion) => {
  const logics = deletedQuestion.questionLogic || [];
  const defaultLogic = logics.find((logic) => isEmptyTarget(logic.questionChoiceIdentifier));
  const target = (defaultLogic || logics[logics.length - 1] || {}).nextQuestionIdentifier;

  // Sin lógica propia, la eliminada sólo seguía el orden de la encuesta.
  if (isEmptyTarget(target)) {
    return `${Number.parseInt(deletedQuestion.identifier, 10) + 1}`;
  }

  return `${target}`;
};

/**
 * Traduce un identificador de la numeración vieja a la nueva, después de haber
 * borrado una pregunta.
 *
 * @param {string|number} identifier Identificador en la numeración vieja.
 * @param {string|number} deletedIdentifier Identificador de la pregunta eliminada.
 * @returns {?string} El identificador nuevo, o `null` si es el de la eliminada.
 */
const remapIdentifierAfterDelete = (identifier, deletedIdentifier) => {
  const current = Number.parseInt(identifier, 10);
  const deleted = Number.parseInt(deletedIdentifier, 10);

  if (Number.isNaN(current) || Number.isNaN(deleted)) return null;
  if (current === deleted) return null;

  return `${current > deleted ? current - 1 : current}`;
};

/**
 * Elimina las filas duplicadas de lógica que puede generar el modal mientras
 * se asignan las alternativas.
 *
 * Sólo se descartan las filas de relleno del modal (las que no tienen ni
 * alternativa ni destino) cuando ya hay un salto por defecto configurado. Las
 * filas con alternativa se conservan tal como están: descartarlas por no tener
 * destino hacía que esa alternativa terminara siguiendo el salto por defecto.
 *
 * No muta el arreglo ni sus elementos.
 *
 * @param {object[]} questionLogic Lógica de una pregunta.
 * @returns {object[]} Lógica normalizada.
 */
const normalizeQuestionLogic = (questionLogic = []) => {
  const hasDefaultTarget = questionLogic.some((logic) => (
    isEmptyTarget(logic.questionChoiceIdentifier) && !isEmptyTarget(logic.nextQuestionIdentifier)
  ));
  const seen = new Set();

  return questionLogic.filter((logic) => {
    const isFiller = isEmptyTarget(logic.questionChoiceIdentifier)
      && isEmptyTarget(logic.nextQuestionIdentifier);
    if (hasDefaultTarget && isFiller) return false;

    const choiceIdentifier = isEmptyTarget(logic.questionChoiceIdentifier)
      ? 'default'
      : `${logic.questionChoiceIdentifier}`;
    if (seen.has(choiceIdentifier)) return false;

    seen.add(choiceIdentifier);
    return true;
  });
};

/**
 * Reacomoda la lógica de una pregunta cuando se borra una de sus alternativas:
 * descarta la fila de la alternativa eliminada y renumera las que quedan, porque
 * los identificadores de alternativa se reasignan 1..N igual que los de las
 * preguntas.
 *
 * Sin esto las conexiones quedaban apuntando a la alternativa equivocada: al
 * borrar la 2 de tres, la fila de la 3 seguía diciendo "3" cuando ya era la 2.
 *
 * No muta el arreglo ni sus elementos.
 *
 * @param {object[]} questionLogic Lógica de la pregunta.
 * @param {string|number} deletedChoiceIdentifier Alternativa que se elimina.
 * @returns {object[]} La lógica con las alternativas renumeradas.
 */
const removeChoiceFromQuestionLogic = (questionLogic = [], deletedChoiceIdentifier) => (
  questionLogic
    .filter((logic) => (
      isEmptyTarget(logic.questionChoiceIdentifier)
      || !sameIdentifier(logic.questionChoiceIdentifier, deletedChoiceIdentifier)
    ))
    .map((logic) => (
      isEmptyTarget(logic.questionChoiceIdentifier)
        ? logic
        : {
          ...logic,
          questionChoiceIdentifier: remapIdentifierAfterDelete(
            logic.questionChoiceIdentifier,
            deletedChoiceIdentifier,
          ),
        }
    ))
);

/**
 * Saca una pregunta de la lista dejando las conexiones consistentes: renumera
 * las que quedan y reengancha los saltos que apuntaban a la eliminada.
 *
 * No muta `pages`: devuelve una lista nueva.
 *
 * @param {object[]} pages Preguntas de la encuesta, en orden.
 * @param {string|number} identifierToDelete Identificador de la pregunta a eliminar.
 * @returns {object[]} Las preguntas que quedan, renumeradas y reenganchadas.
 */
const removeQuestionAndRewireLogic = (pages, identifierToDelete) => {
  const deletedIndex = pages.findIndex(
    (page) => sameIdentifier(page.identifier, identifierToDelete),
  );

  if (deletedIndex === -1) return pages;

  const deletedQuestion = pages[deletedIndex];
  const survivors = pages.filter((page, index) => index !== deletedIndex);
  const inheritedTarget = getInheritedTarget(deletedQuestion);
  // Un destino fuera de rango sigue significando "no apunta a ninguna pregunta".
  const outOfRange = `${survivors.length + 1}`;

  const resolveTarget = (target, alreadyInherited = false) => {
    if (isEmptyTarget(target)) return target;
    if (sameIdentifier(target, END)) return END;

    if (sameIdentifier(target, deletedQuestion.identifier)) {
      // El `alreadyInherited` corta el ciclo si la eliminada se apuntaba a sí misma.
      return alreadyInherited ? outOfRange : resolveTarget(inheritedTarget, true);
    }

    const remapped = remapIdentifierAfterDelete(target, deletedQuestion.identifier);

    if (!remapped || Number.parseInt(remapped, 10) > survivors.length) return outOfRange;

    return remapped;
  };

  return survivors.map((page, index) => {
    const identifier = `${index + 1}`;

    return {
      ...page,
      identifier,
      ...(page.position !== undefined ? { position: identifier } : {}),
      questionLogic: (page.questionLogic || []).map((logic) => ({
        ...logic,
        questionIdentifier: identifier,
        nextQuestionIdentifier: resolveTarget(logic.nextQuestionIdentifier),
      })),
    };
  });
};

export {
  getInheritedTarget,
  remapIdentifierAfterDelete,
  normalizeQuestionLogic,
  removeChoiceFromQuestionLogic,
  removeQuestionAndRewireLogic,
};
